import React, { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../lib/api-client';
import HLS from 'hls.js';

interface Props {
  channelId: string;
  channelName: string;
  onClose: () => void;
}

export function VideoPlayer({ channelId, channelName, onClose }: Props) {
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<HLS | null>(null);

  useEffect(() => {
    const resolveStream = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiFetch<{ url: string }>(`/channels/${channelId}/resolve`);
        setStreamUrl(result.url);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to resolve stream';
        setError(message);
        console.error('Error resolving stream:', err);
      } finally {
        setLoading(false);
      }
    };

    resolveStream();
  }, [channelId]);

  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;

    const setupVideo = () => {
      const video = videoRef.current!;
      const isHLS = streamUrl.includes('.m3u8');

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (isHLS && HLS.isSupported()) {
        const hls = new HLS({
          autoStartLoad: true,
          debug: false,
        });
        hlsRef.current = hls;

        hls.loadSource(streamUrl);
        hls.attachMedia(video);
        hls.on(HLS.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {
            // Autoplay blocked
          });
        });

        hls.on(HLS.Events.ERROR, (event, data) => {
          if (data.fatal) {
            setError(`Stream error: ${data.error?.message || 'Unknown error'}`);
            console.error('HLS error:', data);
          }
        });
      } else if (isHLS && video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = streamUrl;
        video.play().catch(() => {
          // Autoplay blocked
        });
      } else {
        video.src = streamUrl;
        video.play().catch(() => {
          // Autoplay blocked
        });
      }
    };

    setupVideo();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl]);

  return (
    <div className="video-player-modal">
      <div className="video-player-container">
        <div className="video-header">
          <h3>{channelName}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {loading && <div className="video-loading">Loading stream...</div>}
        {error && <div className="video-error">Error: {error}</div>}
        {streamUrl && (
          <video
            ref={videoRef}
            className="video-element"
            controls
            crossOrigin="anonymous"
          />
        )}
      </div>
    </div>
  );
}
